import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, CircularProgress, Backdrop, useTheme, styled, Avatar, TextField,
  Divider, MenuItem, Grid, FormControlLabel, Checkbox
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";

import HomeHeader from "../Components/HomeHeader";
import HomeFooter from "../Components/HomeFooter";
import defaultPfp from "../Components/globalAssets/ppfLogo.png";
import { niches, region } from "../Utils/constants";
import MuiPhoneInput from "../Components/MuiPhoneInput";
import AlertDialog from "../Components/AlertDialog";
import useAlertDialog from "../Components/useAlertDialog";
import client from "../API";

/* ————————————————— styled components ————————————————————————— */
const Frame = styled("section")(({ theme }) => ({
  position: "relative", width: "100%", minHeight: "100vh", overflow: "hidden",
  display: "flex", justifyContent: "center", alignItems: "center", paddingTop: 64,
  [theme.breakpoints.up("sm")]: { paddingTop: 80 }
}));
const SlideBox = styled(Box, { shouldForwardProp: (p)=>p!=="active" && p!=="isFormSlide" })(({ active, isFormSlide })=>({
  position: "absolute", inset: 0, padding: isFormSlide?"clamp(5rem,10vw,6rem) clamp(1rem,5vw,2rem) clamp(1rem,5vw,2rem)":"clamp(1rem,5vw,2rem)",
  display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", textAlign:"center", color:"#fff", opacity:active?1:0,
  transition:"opacity .8s", overflowY:"auto", maxHeight:"100vh"
}));
const NavDots = styled(Box)({ position:"absolute", bottom:".5rem", left:0, right:0, display:"flex", justifyContent:"center", gap:15, zIndex:20 });
const Dot = styled(Box, { shouldForwardProp:(p)=>p!=="active" })(({ theme, active })=>({
  width:12, height:12, borderRadius:"50%", cursor:"pointer", background:active?theme.palette.primary.main:`${theme.palette.primary.main}55`, transition:"transform .25s", transform:active?"scale(1.3)":"scale(1)", "&:hover":{ transform:"scale(1.3)" }
}));
const SLIDE_MS = 3500;
/* ———————————————————————————————————————————————————————————— */

export default function NewCreatorPartner() {
  const navigate = useNavigate();
  const theme = useTheme();
  const timerRef = useRef(null);
  const { referrer } = useParams();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  // form state
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [creatorNiche, setCreatorNiche] = useState([]);
  const [creatorRegion, setCreatorRegion] = useState("");
  const [supervisor] = useState(referrer || "");
  const [termsAccepted, setTermsAccepted] = useState(false);

  // socials
  const [ig, setIg] = useState("");
  const [tt, setTt] = useState("");
  const [yt, setYt] = useState("");
  const [pod, setPod] = useState("");
  const [x, setX] = useState("");
  const [tw, setTw] = useState("");
  const [kk, setKk] = useState("");

  const [loading, setLoading] = useState(false);
  const [referrerData, setReferrerData] = useState(null);

  /* -------------------- Fetch referrer -------------------- */
  useEffect(()=>{ if(!referrer) return; (async()=>{ try{ const resp=await client.users.fetchPublicDetails(referrer); setReferrerData({ name:`${resp.first_name||""} ${resp.last_name||""}`.trim()||resp.username||"Unknown User", avatar:resp.phphref||defaultPfp }); }catch{ setReferrerData({name:"Unknown User", avatar:defaultPfp}); }})(); },[referrer]);

  const buildEditUrl = () => `/creatorconnect/edit?tab=info${referrer?`&referrer=${referrer}`:""}`;

  const alertErr = (msg)=>openDialog("Error", msg, closeDialog, closeDialog, "Ok");

  const isFormValid = () => {
    if (!termsAccepted) { alertErr("You must accept the Terms & Conditions to continue."); return false; }
    if (!username || username.includes(" ")) { alertErr("Username required and cannot contain spaces."); return false; }
    if (!creatorRegion || creatorNiche.length===0) { alertErr("Select region and at least one niche."); return false; }
    if (phone.length < 9) { alertErr("Enter a valid phone number."); return false; }
    if (!ig && !tt && !yt && !pod && !x && !tw && !kk) { alertErr("Provide at least one platform username."); return false; }
    return true;
  };

  /* ---------------- Google OAuth handler ---------------- */
  const handleGoogleSuccess = async (resp) => {
    if(!isFormValid()) return;
    const googleCode = resp?.code || resp?.credential || resp?.authorizationCode;
    if(!googleCode){ alertErr("Google sign‑in did not return a valid authorization code. Please try again."); return; }
    setLoading(true);
    const payload = {
      username, is_partner:true, region:creatorRegion, niche:creatorNiche, supervisor,
      youtube: yt?`https://www.youtube.com/@${yt}`:null, instagram: ig?`https://www.instagram.com/${ig}`:null,
      tiktok: tt?`https://www.tiktok.com/@${tt}`:null, podcast:pod||null, x:x?`https://www.x.com/${x}`:null,
      twitch: tw?`https://www.twitch.tv/${tw}`:null, kick: kk?`https://www.kick.com/${kk}`:null,
      google_code: googleCode, phone_number: phone
    };
    try{
      const res = await client.creatorConnect.onboard(payload);
      if(res?.token){ await client.users.unilogin({ token: res.token }); navigate(buildEditUrl()); return; }
      if(res?.request_id){ alertErr("Almost done! Google granted initial permissions – check your inbox to finish authorizing Blitz."); return; }
      await client.users.unilogin({ token: googleCode }); navigate(buildEditUrl());
    }catch(err){ console.error(err); navigate(buildEditUrl()); alertErr(`Onboarding failed: ${err.response?.data?.error||err.message}`);
    }finally{ setLoading(false); }
  };
  const handleGoogleError = (e)=>{ console.error(e); alertErr("Google sign‑in failed. Please try again."); };

  /* -------------------- Form Slide -------------------- */
  const renderFormSlide = useCallback(()=> (
    <Box sx={{ width:"100%", maxWidth:{xs:"100%", sm:540}, mx:"auto", backgroundColor:"#000", color:"#fff", height:'105%', p:{xs:2, sm:3}, borderRadius:2,
      "& .MuiTextField-root":{ mb:2, "& .MuiInputBase-input":{color:'#fff',fontSize:'clamp(0.9rem,4vw,1rem)'},"& .MuiInputLabel-root":{color:'rgba(255,255,255,0.7)',fontSize:'clamp(0.9rem,4vw,1rem)'},"& .MuiInputBase-root":{backgroundColor:'rgba(255,255,255,0.1)',minHeight:'48px'},"& .MuiInputBase-root:hover":{backgroundColor:'rgba(255,255,255,0.15)'},"& .MuiOutlinedInput-notchedOutline":{borderColor:'rgba(255,255,255,0.3)'}},"& .MuiDivider-root":{backgroundColor:'rgba(255,255,255,0.2)'} }}>
      <Typography variant="h3" sx={{ mb:1, fontWeight:700, fontSize:{xs:'1.8rem', sm:'2.5rem'} }}>Partner Application</Typography>
      <Typography variant="body1" sx={{ opacity:0.85, mb:3, maxWidth:480, mx:'auto', fontSize:{xs:'0.9rem', sm:'1rem'} }}>Blitz needs Gmail access to negotiate on your behalf. Complete the form, then grant permissions in the Google popup.</Typography>

      <TextField label="Public Username" fullWidth required value={username} onChange={e=>setUsername(e.target.value)} />
      <TextField label="Region" select fullWidth required value={creatorRegion} onChange={e=>setCreatorRegion(e.target.value)}>
        {region.map(r=>(<MenuItem key={r} value={r} sx={{ color:'#000', backgroundColor:'#fff', '&:hover':{ backgroundColor:'#f0f0f0' } }}>{r==='NACAUKAUS'? 'USA, Canada, UK, Australia':r}</MenuItem>))}
      </TextField>
      <TextField label="Niche(s)" select fullWidth required SelectProps={{ multiple:true }} value={creatorNiche} onChange={e=>setCreatorNiche(e.target.value)}>
        {niches.map(n=>(<MenuItem key={n} value={n} sx={{ color:'#000', backgroundColor:'#fff', '&:hover':{ backgroundColor:'#f0f0f0' } }}>{n}</MenuItem>))}
      </TextField>

      <MuiPhoneInput country="us" value={phone} onChange={setPhone} preferredCountries={["us"]} isValid={(v)=>v.length>=10} required fullWidth />

      <Divider sx={{ my:2 }} />

      <Grid container spacing={2}>
        {[{label:'Instagram @', value:ig, setter:setIg},{label:'TikTok @', value:tt, setter:setTt},{label:'YouTube @', value:yt, setter:setYt},{label:'X / Twitter @', value:x, setter:setX},{label:'Twitch @', value:tw, setter:setTw},{label:'Kick @', value:kk, setter:setKk},{label:'Podcast name', value:pod, setter:setPod}].map(({label,value,setter})=>(<Grid item xs={12} key={label}><TextField label={label} fullWidth value={value} onChange={e=>setter(e.target.value)} /></Grid>))}
      </Grid>

      <FormControlLabel control={<Checkbox checked={termsAccepted} onChange={(e)=>setTermsAccepted(e.target.checked)} sx={{ color:'rgba(255,255,255,0.5)', '&.Mui-checked':{ color:'#fff' } }} />} label={<Typography sx={{ color:'#fff', fontSize:{xs:'0.85rem', sm:'0.9rem'} }}>I agree to the <a href="/terms_creators" target="_blank" rel="noopener noreferrer" style={{ color:'#6366f1' }}>Terms & Conditions</a></Typography>} sx={{ mt:2 }} />

      <Box sx={{ display:'flex', justifyContent:'center', opacity:termsAccepted?1:0.5, pointerEvents:termsAccepted?'auto':'none' }}>
        <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} flow="auth-code" ux_mode="popup" access_type="offline" prompt="consent" scope="https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email" size="large" width="100%" sx={{ maxWidth:{ xs:'100%', sm:'300px' }, fontSize:'clamp(0.9rem,4vw,1rem)' }} />
      </Box>

      <Typography variant="body2" sx={{ mt:3, mb:2, fontSize:{ xs:'0.85rem', sm:'0.8rem' } }}>
        Click “Sign in with Google” to finish. <strong>You might see two permission screens</strong> – the second one grants Blitz the Gmail access we need.
      </Typography>
    </Box>
  ), [username, creatorRegion, creatorNiche, phone, ig, tt, yt, x, tw, kk, pod, termsAccepted]);

  /* -------------------- Slides -------------------- */
  const slides = useMemo(()=>[
    { bg:"linear-gradient(135deg,#6d00ff,#2d0b63)", custom:(<Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}><Avatar src={referrerData?.avatar||defaultPfp} sx={{ width:{xs:100, sm:140}, height:{xs:100, sm:140}, mb:1, border:'3px solid #fff' }} /><Typography variant="h2" sx={{ fontWeight:800, lineHeight:1.1, fontSize:{ xs:'1.8rem', sm:'clamp(2rem,6vw,3.5rem)' } }}>Welcome Creator!</Typography>{referrerData && (<Typography variant="h5" sx={{ opacity:0.85, maxWidth:{ xs:300, sm:700 }, mb:1, fontSize:{ xs:'1.2rem', sm:'1.5rem' } }}>{referrerData.name} INVITED YOU</Typography>)}<Typography variant="h5" sx={{ opacity:0.85, maxWidth:{ xs:300, sm:700 }, mb:1, fontSize:{ xs:'1.2rem', sm:'1.5rem' } }}>Join the <strong>Blitz Partner Program</strong></Typography></Box>) },
    { bg:"linear-gradient(135deg,#ff4d4d,#7000ff)", title:"All-in-One AI Sales Manager", bullets:["Smart brand matching 24/7","Automated negotiations → better rates","Zero % manager fees"] },
    { bg:"linear-gradient(135deg,#00c9ff,#005bff)", title:"Access Top Brands", bullets:["Global visibility","Instant e-contracts & tracking","Credibility with household names"] },
    { bg:"linear-gradient(135deg,#181818,#000)", custom: renderFormSlide() }
  ], [renderFormSlide, referrerData, theme]);

  const [idx,setIdx] = useState(0);
  useEffect(()=>{ if(idx===slides.length-1) return; timerRef.current=setInterval(()=>setIdx(p=>(p+1)%slides.length), SLIDE_MS); return()=>clearInterval(timerRef.current); },[idx,slides.length]);

  /* -------------------- Render -------------------- */
  return (<>
    <HomeHeader />
    <Frame>
      {slides.map((s,i)=>(<SlideBox key={i} active={i===idx} isFormSlide={i===slides.length-1} sx={{ background:s.bg, gap:{ xs:2, sm:3 } }}>{s.custom? s.custom : (<><Typography variant="h2" sx={{ fontSize:{ xs:'1.8rem', sm:'clamp(2rem,6vw,3.5rem)' }, fontWeight:800, lineHeight:1.1 }}>{s.title}</Typography>{s.bullets && (<Box component="ul" sx={{ listStyle:'none', p:0, m:0, display:'flex', flexDirection:'column', gap:1, maxWidth:{ xs:300, sm:620 } }}>{s.bullets.map(b=>(<Typography component="li" key={b} sx={{ '&::before':{ content:'"•"', mr:1, color: theme.palette.primary.light }, fontSize:{ xs:'0.9rem', sm:'clamp(1rem, 2.5vw, 1.25rem)' } }}>{b}</Typography>))}</Box>)}</>)}</SlideBox>))}
      <NavDots>{slides.map((_,i)=><Dot key={i} active={i===idx} onClick={()=>setIdx(i)} />)}</NavDots>
    </Frame>
    <HomeFooter />
    <Backdrop sx={{ color:'#fff', zIndex:(t)=>t.zIndex.drawer+1 }} open={loading}><CircularProgress color="inherit" /></Backdrop>
    <AlertDialog alertState={dialogState} />
  </>);
}
